/**
 * Navigator class for programmatic navigation in JoyDoc.
 * Users instantiate this and pass it to JoyDoc component.
 *
 * @example
 * import { JoyDoc, Navigator } from '@joyfill/components';
 *
 * const navigator = new Navigator();
 *
 * <JoyDoc navigator={navigator} doc={doc} mode="fill" />
 *
 * // Navigate to page
 * navigator.goto('pageId');
 *
 * // Navigate to page and scroll to field
 * navigator.goto('pageId/fieldPositionId');
 */
class Navigator {
  constructor() {
    this._handler = null;
  }

  /**
   * Called internally by File.js to register its navigation handler
   * @private
   * @param {Function} handler - Navigation handler function
   */
  _register(handler) {
    this._handler = handler;
  }

  /**
   * Called internally by File.js when it unmounts
   * @private
   */
  _unregister() {
    this._handler = null;
  }

  /**
   * Navigate to a page and optionally scroll to a field
   * @param {string} path - "pageId" or "pageId/fieldPositionId"
   * @returns {{ status: 'success' | 'failure' }}
   */
  goto(path) {
    if (!this._handler) {
      console.warn('Navigator: No JoyDoc component connected');
      return { status: 'failure' };
    }
    return this._handler(path);
  }
}

/**
 * Parses navigation path string.
 * @param {string} path - "pageId" or "pageId/fieldPositionId"
 * @returns {{ pageId: string|null, fieldPositionId: string|null }}
 */
export const parseNavigationPath = (path) => {
  if (!path || typeof path !== 'string') {
    return { pageId: null, fieldPositionId: null };
  }
  const parts = path.split('/');
  return {
    pageId: parts[0] || null,
    fieldPositionId: parts[1] || null,
  };
};

export default Navigator;
